import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import * as XLSX from 'xlsx-js-style';

/**
 * Bank statement file importer for the bank-rec screen.
 *
 * Drop an OFX/QFX, CSV, Excel, BAI2 or PDF statement, pick the ERP bank account it
 * belongs to, review what the server parsed, then import. Rows land in the same
 * bank_feed_transactions table the SimpleFIN bridge writes, so matching, AR
 * proposals and posting all work on them untouched — the point being that a dead
 * bridge connection can never block a reconciliation.
 *
 * Preview writes nothing. Only rows the operator leaves checked are imported, and
 * anything that looks like a duplicate arrives unchecked.
 */
@Component({
	selector: 'app-statement-import',
	templateUrl: './statement-import.component.html',
	styleUrls: ['./statement-import.component.scss']
})
export class StatementImportComponent implements OnChanges, OnInit {
	@Input() bankaccounts: any[] = [];
	@Input() defaultBank = '';
	@Output() imported = new EventEmitter<any>();

	mode: 'input' | 'review' | 'done' = 'input';
	bankaccount = '';
	fileName = '';
	fileBase64 = '';

	parsing = false;
	importing = false;
	error = '';

	preview: any = null;          // server preview payload
	rows: any[] = [];             // classified rows, each with .accept
	result: any = null;           // commit result
	dragging = false;
	showDups = false;             // duplicates are collapsed by default
	reloadedFrom: number | null = null; // set when the current review came from history, not a fresh upload

	// CSV column mapping — only surfaced when the file needed it.
	headers: string[] = [];
	mapping: any = { date: '', amount: '', debit: '', credit: '', description: '', invert: 0 };
	remember = false;

	// Past drops — filename, date, what happened, and (when stored) a Reload action.
	history: any[] = [];
	historyLoading = false;
	reloadingId: number | null = null;
	undoingId: number | null = null;

	readonly accept = '.ofx,.qfx,.qbo,.csv,.tsv,.txt,.xls,.xlsx,.pdf,.bai,.bai2';

	constructor(private paymentsService: PaymentsService, private globalSearchService: GlobalSearchService) {}

	ngOnInit(): void {
		this.loadHistory();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['defaultBank'] && !this.bankaccount && this.defaultBank) {
			this.bankaccount = this.defaultBank;
		}
	}

	/* --------------------------------------------------------------- history */

	bankaccountName(code: string): string {
		const b = this.bankaccounts.find((a) => a.accountcode === code);
		return b ? b.bankaccountname : code;
	}

	loadHistory(): void {
		this.historyLoading = true;
		this.paymentsService.statementHistory({ limit: 25 }).subscribe(
			(res: any) => {
				this.historyLoading = false;
				this.history = (res && res.success) ? (res.drops || []) : [];
			},
			() => { this.historyLoading = false; }
		);
	}

	/** Re-read a past drop's stored file and drop the operator into the same review screen. */
	reload(drop: any): void {
		if (!drop || !drop.has_file || this.reloadingId) { return; }
		this.reloadingId = drop.id;
		this.error = '';
		this.paymentsService.statementReload({ id: drop.id }).subscribe((res: any) => {
			this.reloadingId = null;
			if (res && res.success) {
				this.bankaccount = res.bankaccount;
				this.fileName = res.sourceName;
				this.fileBase64 = ''; // reloaded from storage — nothing new to persist on re-commit
				this.reloadedFrom = drop.id;
				this.preview = res;
				this.rows = res.rows || [];
				this.headers = res.headers || [];
				if (res.mapping) { this.mapping = { ...this.mapping, ...res.mapping }; }
				this.mode = 'review';
			} else {
				this.globalSearchService.showNotification(res?.error || 'Could not reload that statement.', 'danger', 'bottom', 'right');
			}
		}, () => {
			this.reloadingId = null;
			this.globalSearchService.showNotification('Could not reload that statement.', 'danger', 'bottom', 'right');
		});
	}

	/**
	 * Reverse a drop — deletes the feed rows it wrote and un-clears any real
	 * banktrans it ended up matched to. Irreversible from here (there's no "redo"),
	 * so confirm first, same as the SimpleFIN disconnect action on this screen.
	 */
	undo(drop: any): void {
		if (!drop || !drop.can_undo || drop.undone_at || this.undoingId) { return; }
		const label = drop.imported_count + ' row(s) from "' + drop.source_name + '"'
			+ (drop.updated_count ? ' (' + drop.updated_count + ' more refreshed)' : '');
		if (!confirm('Undo this drop? ' + label + ' will be removed from the bank feed, and any of them already matched will be un-cleared on the Reconciliation tab. This cannot be redone.')) {
			return;
		}
		this.undoingId = drop.id;
		this.paymentsService.statementUndo({ id: drop.id }).subscribe((res: any) => {
			this.undoingId = null;
			if (res && res.success) {
				drop.undone_at = new Date().toISOString();
				let msg = res.removed + ' row(s) removed.';
				if (res.uncleared) { msg += ' ' + res.uncleared + ' un-cleared on the Reconciliation tab.'; }
				if (res.lockedSkipped) { msg += ' ' + res.lockedSkipped + ' left cleared — that bank-rec period is locked.'; }
				this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
				this.imported.emit({ imported: 0, undone: true }); // tell the parent to refresh its feed view too
			} else {
				this.globalSearchService.showNotification(res?.error || 'Could not undo that drop.', 'danger', 'bottom', 'right');
			}
		}, () => {
			this.undoingId = null;
			this.globalSearchService.showNotification('Could not undo that drop.', 'danger', 'bottom', 'right');
		});
	}

	/* ----------------------------------------------------------- file intake */

	onDragOver(event: DragEvent): void { event.preventDefault(); this.dragging = true; }
	onDragLeave(event: DragEvent): void { event.preventDefault(); this.dragging = false; }
	onDrop(event: DragEvent): void {
		event.preventDefault();
		this.dragging = false;
		this.acceptFile(event.dataTransfer?.files?.[0]);
	}
	onFile(event: any): void { this.acceptFile(event?.target?.files?.[0]); }

	private acceptFile(file: File | null | undefined): void {
		if (!file) { return; }
		this.error = '';
		const lower = file.name.toLowerCase();
		const known = ['.ofx', '.qfx', '.qbo', '.csv', '.tsv', '.txt', '.xls', '.xlsx', '.pdf', '.bai', '.bai2'];
		if (!known.some((e) => lower.endsWith(e))) {
			this.globalSearchService.showNotification('Unsupported file — drop an OFX/QFX, CSV, Excel, BAI2 or PDF statement.', 'warning', 'bottom', 'right');
			return;
		}
		this.fileName = file.name;

		// Excel is flattened to CSV in the browser, same as remittance-import does,
		// so the server only ever deals with delimited text.
		if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const wb = XLSX.read(new Uint8Array(reader.result as ArrayBuffer), { type: 'array' });
					const ws = wb.Sheets[wb.SheetNames[0]];
					const csv = XLSX.utils.sheet_to_csv(ws);
					this.fileBase64 = 'data:text/csv;base64,' + btoa(unescape(encodeURIComponent(csv)));
					// Keep a .csv name so the server reads it down the delimited path.
					this.fileName = file.name.replace(/\.xlsx?$/i, '.csv');
					this.parse();
				} catch (e) {
					this.clearFile();
					this.globalSearchService.showNotification('Could not read that Excel file.', 'danger', 'bottom', 'right');
				}
			};
			reader.readAsArrayBuffer(file);
			return;
		}

		const reader = new FileReader();
		reader.onload = () => {
			this.fileBase64 = String(reader.result || '');
			this.parse();
		};
		reader.readAsDataURL(file);
	}

	clearFile(): void {
		this.fileName = '';
		this.fileBase64 = '';
		this.headers = [];
		this.preview = null;
		this.rows = [];
		this.error = '';
		this.reloadedFrom = null;
		this.mode = 'input';
	}

	/* --------------------------------------------------------------- preview */

	canParse(): boolean {
		return !this.parsing && !!this.bankaccount && !!this.fileBase64;
	}

	parse(remap = false): void {
		if (!this.bankaccount) {
			this.globalSearchService.showNotification('Pick the ERP bank account this statement belongs to first.', 'warning', 'bottom', 'right');
			return;
		}
		if (!this.fileBase64) { return; }

		const payload: any = {
			bankaccount: this.bankaccount,
			file: { name: this.fileName, content: this.fileBase64 },
		};
		// Only send a mapping once the operator has actually chosen one, so the
		// server's sniff/saved profile wins on the first pass.
		if (remap) { payload.mapping = this.mapping; }

		this.parsing = true;
		this.error = '';
		this.paymentsService.statementPreview(payload).subscribe((res: any) => {
			this.parsing = false;
			if (res && res.success) {
				this.preview = res;
				this.rows = res.rows || [];
				this.headers = res.headers || [];
				if (res.mapping) { this.mapping = { ...this.mapping, ...res.mapping }; }
				this.mode = 'review';
			} else {
				// A mapping failure still returns headers, so the operator can map
				// the columns and retry without re-dropping the file.
				this.error = res?.error || 'Could not read that statement.';
				this.headers = res?.headers || [];
				if (res?.mapping) { this.mapping = { ...this.mapping, ...res.mapping }; }
				this.mode = this.headers.length ? 'review' : 'input';
			}
		}, () => {
			this.parsing = false;
			this.error = 'Statement parse failed.';
		});
	}

	get parsingLabel(): string {
		return /\.pdf$/i.test(this.fileName) ? 'Reading the PDF statement…' : 'Parsing the statement…';
	}

	/* ---------------------------------------------------------------- review */

	get newRows(): any[] { return this.rows.filter((r) => r.status === 'new'); }
	get dupRows(): any[] { return this.rows.filter((r) => r.status !== 'new'); }
	get acceptedRows(): any[] { return this.rows.filter((r) => r.accept); }

	get acceptedTotal(): number {
		return Math.round(this.acceptedRows.reduce((a, r) => a + Number(r.amount || 0), 0) * 100) / 100;
	}

	toggleAll(on: boolean): void {
		// Only ever bulk-toggles the genuinely-new rows: bulk-accepting suspected
		// duplicates is exactly the mistake this screen exists to prevent.
		this.newRows.forEach((r) => { r.accept = on; });
	}

	statusLabel(status: string): string {
		if (status === 'dup_exact') { return 'Already imported'; }
		if (status === 'dup_overlap') { return 'Already in the feed'; }
		return 'New';
	}

	statusHint(status: string): string {
		if (status === 'dup_exact') { return 'This exact line was imported before — re-importing does nothing.'; }
		if (status === 'dup_overlap') { return 'A line with this date and amount is already in the feed from another source (e.g. the bank bridge). Importing it would double-count.'; }
		return '';
	}

	get balanceCheck(): any { return this.preview?.balanceCheck || null; }

	/** Only shown for PDFs, where an AI read can silently drop lines. */
	get balanceMismatch(): boolean {
		const b = this.balanceCheck;
		return !!(b && b.checked && !b.ok);
	}

	needsMapping(): boolean {
		return this.preview?.format === 'csv' && this.headers.length > 0;
	}

	remap(): void {
		this.parse(true);
		if (this.remember && this.bankaccount) {
			this.paymentsService.statementSaveMapping({ bankaccount: this.bankaccount, mapping: this.mapping })
				.subscribe(() => { /* profile stored on bankaccounts.importformat */ });
		}
	}

	/* ---------------------------------------------------------------- commit */

	commit(): void {
		const rows = this.acceptedRows;
		if (!rows.length) {
			this.globalSearchService.showNotification('Nothing checked to import.', 'warning', 'bottom', 'right');
			return;
		}
		this.importing = true;
		// Span of what we're importing. The bank-rec feed pane reloads against its
		// own date range, so a statement covering earlier months imports fine and
		// is then invisible — the operator sees the pre-import table and reads it
		// as "nothing happened". Hand the parent the dates so it can widen.
		const posted = rows.map((r: any) => String(r.posted || '')).filter((d: string) => !!d).sort();
		const payload: any = {
			bankaccount: this.bankaccount,
			rows: rows,
			source_name: this.fileName,
		};
		// Only a fresh upload carries bytes to persist — a reload's file is already
		// on disk from its original drop, no need to write a second copy of it.
		if (this.fileBase64) {
			payload.file = { name: this.fileName, content: this.fileBase64 };
		}
		if (this.balanceCheck && this.balanceCheck.checked) {
			payload.balance = { opening: this.balanceCheck.opening, closing: this.balanceCheck.closing };
		}
		this.paymentsService.statementCommit(payload).subscribe((res: any) => {
			this.importing = false;
			if (res && res.success) {
				this.result = res;
				this.mode = 'done';
				this.globalSearchService.showNotification(
					res.imported + ' transaction(s) imported into the feed.', 'success', 'bottom', 'right');
				this.imported.emit({
					...res,
					bankaccount: this.bankaccount,
					postedFrom: posted.length ? posted[0] : '',
					postedTo:   posted.length ? posted[posted.length - 1] : '',
				});
				this.loadHistory();
			} else {
				this.globalSearchService.showNotification(res?.error || 'Import failed.', 'danger', 'bottom', 'right');
			}
		}, () => {
			this.importing = false;
			this.globalSearchService.showNotification('Import failed.', 'danger', 'bottom', 'right');
		});
	}

	reset(): void {
		this.clearFile();
		this.result = null;
		this.showDups = false;
	}
}
